import { Box, Typography } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import RemoveBg from '../assets/icons/canvasIcons/remove-bg.tsx';
import ReplaceBg from '../assets/icons/canvasIcons/replace-bg.tsx';
import Erase from '../assets/icons/canvasIcons/erase.tsx';
import inpaint from '../assets/icons/canvasIcons/inpaint.tsx';
import outpaint from '../assets/icons/canvasIcons/outpaint.tsx';
import upscale from '../assets/icons/canvasIcons/upscale.tsx';
import deblur from '../assets/icons/canvasIcons/deblur.tsx';
import denoise from '../assets/icons/canvasIcons/denoise.tsx';
import retouch from '../assets/icons/canvasIcons/retouch.tsx';
import GridViewIcon2 from "../icons/GridViewIcon2.tsx";
import ListViewIcon from "../icons/ListViewIcon.tsx";
import GenerateIcon from "../icons/GenerateIcon.tsx";


interface EditorToolbarProps {
  exitBoolean?: boolean;
  Exiticon: string;
  clearSession: () => void;
  iconList?:[];    
  activeTool?: string | null;
  isGenerating?: boolean;
  isOutpaintActive: boolean;
  setActiveTool: React.Dispatch<React.SetStateAction<string | null>>;
  setIsOutpaintActive: React.Dispatch<React.SetStateAction<boolean>>;
  setHasOutpaintAspect: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOutpaintFrameLocked: React.Dispatch<React.SetStateAction<boolean>>;
  removeBackground: () => void;
  upscaleImage: () => void;
  deblurImage: () => void;
  denoiseImage: () => void;
  retouchImage: () => void;
  viewMode?: 'grid' | 'list';
  setViewMode?: React.Dispatch<React.SetStateAction<'grid' | 'list'>>;
  contentTypeTab?: 'image' | 'video';
}

export default function EditorToolbar({
  exitBoolean,
  Exiticon,
  clearSession,
  activeTool,
  isGenerating,
  isOutpaintActive,
  setActiveTool,
  setIsOutpaintActive,
  setHasOutpaintAspect,
  setIsOutpaintFrameLocked,
  removeBackground,
  upscaleImage,
  deblurImage,
  denoiseImage,
  retouchImage,
  viewMode = 'list',
  setViewMode,
  contentTypeTab = 'image',
}: EditorToolbarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isCanvasEditor = location.pathname.includes('/canvas-ai-editor');
  
  const allIcons =[
    { Icon: GenerateIcon, title: 'Generate', isMultiStep: false },
    { Icon: RemoveBg, title: 'Remove Bg', isMultiStep: false },
    { Icon: ReplaceBg, title: 'Replace Bg', isMultiStep: true },
    { Icon: Erase, title: 'Erase', isMultiStep: true },
    { Icon: inpaint, title: 'Inpaint', isMultiStep: true },
    { Icon: outpaint, title: 'Outpaint', isMultiStep: true },
    { Icon: upscale, title: 'Upscale', isMultiStep: false },
    { Icon: deblur, title: 'Deblur', isMultiStep: false },
    { Icon: denoise, title: 'Denoise', isMultiStep: false },
    { Icon: retouch, title: 'Retouch', isMultiStep: false }
  ];
  
  // For canvas-ai-editor: show 9 tools without Generate
  // For other routes: show 10 tools with Generate
  // For video mode: hide all icons
  const iconList = contentTypeTab === 'video'
    ? []
    : isCanvasEditor
      ? allIcons.filter(icon => icon.title !== 'Generate')
      : allIcons;

  const iconList2 =[
    { Icon: GridViewIcon2, title: 'Grid View', isMultiStep: false },
    { Icon: ListViewIcon, title: 'List View', isMultiStep: true }
  ];

  // Helper function to check if a tool should be highlighted
  const isToolActive = (title: string) => {
    return activeTool === title;
  };

  const handleIconClick = (title: string) => {
    if (isGenerating) return;

    const clickedIcon = allIcons.find(icon => icon.title === title);
    
    if (title !== "Outpaint" && isOutpaintActive) {
      setIsOutpaintActive(false);
      setHasOutpaintAspect(false);
      setIsOutpaintFrameLocked(false);
    }

    if (title === "Outpaint") {
      setActiveTool("Outpaint");
      setIsOutpaintActive(true);
      setHasOutpaintAspect(true);
    } else {
      // For single-click operations, set the tool as active (will show green while processing)
      // For multi-step operations, toggle the tool
      if (clickedIcon?.isMultiStep) {
        setActiveTool((prev) => (prev === title ? null : title));
      } else {
        // Single-click operation: set it active, it will reset to "Generate" after completion
        setActiveTool(title);
      }
    }

    if (title === "Remove Bg") removeBackground();
    else if (title === "Upscale") upscaleImage();
    else if (title === "Deblur") deblurImage();
    else if (title === "Denoise") denoiseImage();
    else if (title === "Retouch") retouchImage();
  };

  // Conditional rendering based on route
  if (isCanvasEditor) {
    // Layout 1: '/canvas-ai-editor' - Exit button (teal) + 9 tools (lime-green)
    return (
      <Box sx={{ display: "flex", mb: 4 ,justifyContent: "space-between", gap:22}}>
        {/* Left Section: Exit Button (Teal-Blue) */}
        {exitBoolean && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              ml: 2,
              cursor: "pointer",
              px: 2,
            }}
            onClick={() => navigate(-1)}
          >
            <Box
              component="img"
              src={Exiticon}
              alt="Back"
              sx={{ width: 19, height: 19 }}
              onClick={clearSession}
            />
            <Typography sx={{ color: '#FAFAFA', fontSize: '14px' }}>Exit</Typography>
          </Box>
        )}

        {/* Right Section: Tool Icons (Lime-Green) */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            flex: 1,
            px: 2,
          }}
        >
          {iconList.map(({ Icon, title }, index) => (
            <Box
              key={index}
              onClick={() => handleIconClick(title)}
              textAlign="center"
              sx={{
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                fontWeight: 400,
                fontSize: "14px",
                fill: activeTool === title ? "#00E7E7" : "inherit",
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 14,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  "& svg": {
                    width: 16,
                    height: 14,
                    fill: activeTool === title ? "#00E7E7" : "#FAFAFA",
                  },
                }}
              >
                <Icon fill={activeTool === title ? "#00E7E7" : "#FAFAFA"} />
              </Box>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: activeTool === title ? "#00E7E7" : "#FAFAFA",
                  fontFamily: "Inter",
                }}
              >
                {title}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Layout 2: Other routes - Empty left (teal/cyan) + 10 tools (olive-green) + Grid/List buttons (black)
  return (
    <Box sx={{ display: "flex", mb:2}}>
      {/* Left Section: Empty/Separator (Teal/Cyan) */}
      

      {/* Middle Section: Tool Icons (Olive Green) */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flex: 1,
          px: 2,
          // backgroundColor: "#1414e7ff",
        }}
      >
        {/* Main Tools */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap:'24px',
            // backgroundColor: "#dede33ff",
            flex: 1,            
            justifyContent: "center", 
          }}
        >
          {iconList.map(({ Icon, title }, index) => {
            const isActive = isToolActive(title);
            // Show green color when tool is active and generating, otherwise show default active color
            const activeColor = isActive && isGenerating ? "#00E7E7" : (isActive ? "#00E7E7" : "#FAFAFA");
            return (
              <Box
                key={index}
                onClick={() => handleIconClick(title)}
                textAlign="center"
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  fontWeight: 400,
                  fontSize: "14px",
                  fill: isActive ? activeColor : "inherit",
                }}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 14,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "& svg": {
                      width: 16,
                      height: 14,
                      fill: activeColor,
                    },
                  }}
                >
                  <Icon fill={activeColor} />
                </Box>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: activeColor,
                    fontFamily: "Inter",
                  }}
                >
                  {title}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Right Section: Grid/List View Buttons (Black) - Hidden for video models */}
        {contentTypeTab === 'video' ? (
          // Spacer to maintain layout position when buttons are hidden for video
          <Box
            sx={{
              width: 76,
              height: 34, // 2 buttons (34px each) + gap (8px) = 76px
              marginRight: "15px",
            }}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginRight: "15px",
              // backgroundColor: "#1fe714ff",
            }}
          >
            {iconList2.map(({ Icon, title }, index) => {
              const isActive = (title === 'Grid View' && viewMode === 'grid') || (title === 'List View' && viewMode === 'list');
              return (
                <Box
                  key={index}
                  onClick={() => {
                    if (setViewMode && !isGenerating) {
                      if (title === 'Grid View') {
                        setViewMode('grid');
                      } else if (title === 'List View') {
                        setViewMode('list');
                      }
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 34,
                    height: 34,
                    cursor: isGenerating ? 'not-allowed' : 'pointer',
                    border: '1px solid #262626',
                    borderRadius: "6px",
                    backgroundColor: '#111113',
                    boxSizing: "border-box",
                    opacity: isGenerating ? 0.5 : 1,
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: '#555',
                      backgroundColor: '#1a1a1a',
                    }
                  }}
                >
                  <Icon fill={isActive ? '#00E7E7' : '#FAFAFA'} width={20} height={20} />
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}
